import React, { useEffect, useState, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";

/**
 * Reusable right-side drawer that fetches a paginated list from a backend
 * endpoint and renders it with a search box + CSV export.
 *
 * Props
 *  - open: bool
 *  - onOpenChange: fn(bool)
 *  - title: string
 *  - subtitle: string
 *  - endpoint: "/admin/dashboard/users"   (no /api prefix)
 *  - params: object        (query string passed to the GET)
 *  - columns: [{ key, label, render?(row) }]
 *  - csvFilename: string   (default "export.csv")
 *  - emptyHint: string
 */
export default function DrillDownDrawer({
  open,
  onOpenChange,
  title,
  subtitle,
  endpoint,
  params = {},
  columns,
  csvFilename = "export.csv",
  emptyHint = "No records match these filters.",
  onRowClick,
}) {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const merged = { ...params };
      if (search.trim()) merged.q = search.trim();
      const r = await api.get(endpoint, { params: merged });
      setItems(r.data.items || []);
      setCount(r.data.count ?? (r.data.items || []).length);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load list");
    } finally {
      setLoading(false);
    }
     
  }, [open, endpoint, JSON.stringify(params), search]);

  useEffect(() => { load(); }, [load]);

  // Debounce search-driven reloads
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => { load(); }, 250);
    return () => clearTimeout(t);
     
  }, [search]);

  const exportAs = async (fmt) => {
    try {
      const qs = new URLSearchParams({ ...params, format: fmt, ...(search ? { q: search } : {}) }).toString();
      const url = `${API}${endpoint}?${qs}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const baseName = csvFilename.replace(/\.[^.]+$/, "");
      const ext = fmt === "csv" ? "csv" : fmt === "xlsx" ? "xlsx" : "pdf";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${baseName}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`Exported ${a.download}`);
    } catch (e) {
      toast.error(`${fmt.toUpperCase()} export failed`);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="!max-w-none w-full sm:w-[680px] md:w-[820px] p-0 overflow-y-auto bg-surface"
        data-testid="drilldown-drawer"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-line">
          <div className="label-tag mb-1">Drill-down</div>
          <SheetTitle className="font-serif text-2xl tracking-tight text-ink">{title}</SheetTitle>
          {subtitle && (
            <SheetDescription className="text-[12px] font-mono text-ink2">
              {subtitle}
            </SheetDescription>
          )}
        </SheetHeader>

        {/* Toolbar */}
        <div className="px-6 py-3 flex items-center gap-2 border-b border-line bg-bg/40">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            data-testid="drilldown-search"
            className="flex-1 px-3 py-2 border border-line bg-surface text-sm font-mono focus:outline-none focus:border-ink"
          />
          <span className="text-[11px] font-mono text-ink2">
            {loading ? "Loading…" : `${count} record${count === 1 ? "" : "s"}`}
          </span>
          <button
            onClick={() => exportAs("csv")}
            disabled={loading || count === 0}
            data-testid="drilldown-csv"
            className="px-3 py-2 border border-line text-[11px] font-mono uppercase tracking-wider hover:bg-ink hover:text-white disabled:opacity-40"
          >
            CSV ↓
          </button>
          <button
            onClick={() => exportAs("xlsx")}
            disabled={loading || count === 0}
            data-testid="drilldown-xlsx"
            className="px-3 py-2 border border-line text-[11px] font-mono uppercase tracking-wider hover:bg-ink hover:text-white disabled:opacity-40"
          >
            XLSX ↓
          </button>
          <button
            onClick={() => exportAs("pdf")}
            disabled={loading || count === 0}
            data-testid="drilldown-pdf"
            className="px-3 py-2 border border-line text-[11px] font-mono uppercase tracking-wider hover:bg-ink hover:text-white disabled:opacity-40"
          >
            PDF ↓
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-surface z-10 border-b border-line">
              <tr className="text-left">
                {columns.map((c) => (
                  <th key={c.key} className="px-4 py-3 text-[11px] tracking-[0.2em] uppercase text-ink2 font-mono">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="px-6 py-12 text-center text-ink2 text-sm">
                    {emptyHint}
                  </td>
                </tr>
              )}
              {items.map((row, i) => {
                const clickable = typeof onRowClick === "function";
                return (
                  <tr
                    key={row.id || row.ref || row.user_id || `row-${i}`}
                    onClick={clickable ? () => onRowClick(row) : undefined}
                    className={`border-b border-line/60 hover:bg-bg/40 ${clickable ? "cursor-pointer" : ""}`}
                    data-testid={clickable ? `drilldown-row-${i}` : undefined}
                  >
                    {columns.map((c) => (
                      <td key={c.key} className="px-4 py-3 text-ink">
                        {c.render ? c.render(row) : (row[c.key] ?? "—")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="h-6" />
      </SheetContent>
    </Sheet>
  );
}

/* Common cell helpers ----------------------------------------------------- */
export const cellDate = (iso) => iso ? new Date(iso).toLocaleDateString() : "—";
export const cellDateTime = (iso) => iso ? new Date(iso).toLocaleString() : "—";
export const cellUsd = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
export const cellMono = (s) => <span className="font-mono text-[12px]">{s ?? "—"}</span>;
export const cellBadge = (label, tone = "muted") => {
  const cls =
    tone === "leaf" ? "bg-leaf/15 text-leaf border-leaf/40" :
    tone === "danger" ? "bg-danger/15 text-danger border-danger/40" :
    tone === "ink" ? "bg-ink text-white border-ink" :
    "bg-muted text-ink border-line";
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border ${cls}`}>
      {label || "—"}
    </span>
  );
};
